import { Input } from '@modules/shadcn/ui/Input/Input'

import { ColorChip } from './ColorChip'
import { colorTones, getColorToneLabel } from './colorOptions'
import { SelectableContainer } from './SelectableContainer'
import type { ColorFilterState } from './useColorFilter'

interface ColorFilterControlsProps {
  filter: ColorFilterState
  /** 検索入力欄の id（同一ページに複数フィルタがある場合の重複回避） */
  searchInputId?: string
  /**
   * メーカー / トーンのチップ列の折返し方針.
   * - `'scroll'`（デフォルト）: 横スクロール（狭いダイアログ = ColorPalette の見た目維持）
   * - `'wrap'`: `flex-wrap` で折返し表示（全幅ページ = プリセット編集で全メーカー・全トーンを一望）
   */
  chipLayout?: 'scroll' | 'wrap'
  /**
   * 「色名・色コードで絞り込む」検索節を描画しない（default: false）.
   * 検索入力を外側で別途持つ場合（プリセット編集の左ペイン）に true にする.
   */
  hideSearch?: boolean
}

/**
 * メーカー選択 / カラートーン絞り込み / 色名・色コード検索の共通フィルタ UI.
 *
 * ColorPalette（色指定パレット）と CompanyColorPresetEditor の右ペイン（追加できる色）で
 * 共通利用する. フィルタ状態は useColorFilter フックから props で受け取る（本コンポーネントは
 * presentational）. `chipLayout` でチップ列を横スクロール / 折返しのどちらにするか切替える.
 */
export const ColorFilterControls = ({
  filter,
  searchInputId = 'color-search',
  chipLayout = 'scroll',
  hideSearch = false,
}: ColorFilterControlsProps) => {
  const {
    makers,
    selectedMaker,
    clearMaker,
    toggleMaker,
    selectedTone,
    toggleTone,
    searchTerm,
    setSearchTerm,
  } = filter

  // wrap: 折返しで全チップを一望 / scroll: 横スクロール（従来の狭ダイアログ用）.
  const chipRowClass =
    chipLayout === 'wrap' ? 'flex flex-wrap gap-2' : '-mx-4 overflow-x-auto px-4 pb-1'
  const chipInnerClass = chipLayout === 'wrap' ? 'contents' : 'flex w-max gap-2'

  return (
    <div className="shrink-0 space-y-4">
      {/* メーカーを指定 */}
      <section className="space-y-2">
        <h3 className="text-xs font-medium text-[#18181B]">メーカーを指定</h3>
        <div className={chipRowClass}>
          <div className={chipInnerClass}>
            {/* メーカー絞り込みを解除して全色に戻す「すべて」チップ */}
            <SelectableContainer onClick={clearMaker} selected={selectedMaker === null}>
              <span className="text-base font-medium text-[#09090B]">すべて</span>
            </SelectableContainer>
            {makers.map(maker => (
              <SelectableContainer
                key={maker}
                onClick={() => toggleMaker(maker)}
                selected={selectedMaker === maker}
              >
                <span className="text-base font-medium text-[#09090B]">{maker}</span>
              </SelectableContainer>
            ))}
          </div>
        </div>
      </section>

      {/* カラートーンで絞り込む（常時表示） */}
      <div className="space-y-2">
        <h3 className="text-xs font-medium text-[#18181B]">
          カラートーンで絞り込む
          {selectedTone ? `（${getColorToneLabel(selectedTone)}）` : ''}
        </h3>
        <div className={chipRowClass}>
          <div className={chipInnerClass}>
            {colorTones.map(({ tone, label, color }) => (
              <SelectableContainer
                key={tone}
                onClick={() => toggleTone(tone)}
                selected={selectedTone === tone}
              >
                <ColorChip color={color} size="md" />
                <span className="text-base font-medium text-[#09090B]">{label}</span>
              </SelectableContainer>
            ))}
          </div>
        </div>
      </div>

      {/* 色名・色コードで絞り込む（hideSearch=true のときは外側の検索入力に委ねる） */}
      {!hideSearch && (
        <div className="max-w-xs space-y-2">
          <label className="text-xs font-medium text-[#18181B]" htmlFor={searchInputId}>
            色名・色コードで絞り込む
          </label>
          <Input
            className="h-9"
            id={searchInputId}
            onChange={e => setSearchTerm(e.target.value)}
            type="text"
            value={searchTerm}
          />
        </div>
      )}
    </div>
  )
}
